// Mobile attendance tab (see `features/mobile-admin.md`): picks a training
// class, offers in-place enrolment, and renders the shared
// `AttendanceCalendar` — the week-strip marking surface also used by the
// desktop `TrainingAttendanceDialog`.

import { useMemo, useState } from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import type {
  Athlete,
  TrainingClass,
  TrainingClassEnrollment,
} from '../domain';
import type { TrainingClassesSnapshot } from '../data/apiClient';
import { useTranslation } from '../state/I18nContext';
import { AttendanceCalendar } from './AttendanceCalendar';
import { TrainingClassMemberDialog } from './TrainingClassMemberDialog';

interface MobileAttendanceViewProps {
  /** The company's training classes (the picker offers them all). */
  classes: TrainingClass[];
  /** Live enrolments across all classes; filtered per selected class here. */
  enrollments: TrainingClassEnrollment[];
  /** The company roster, to resolve enrolled athletes' names. */
  athletes: Athlete[];
  /** Whether the signed-in user may create sessions and mark attendance. */
  canManage: boolean;
  /** Applies a refreshed company snapshot after an enrolment is added here. */
  onSnapshot: (snapshot: TrainingClassesSnapshot) => void;
}

export function MobileAttendanceView({
  classes,
  enrollments,
  athletes,
  canManage,
  onSnapshot,
}: MobileAttendanceViewProps) {
  const { t } = useTranslation();

  // The picked class; falls back to the first class until the user chooses.
  const [classChoice, setClassChoice] = useState<number | null>(null);
  const selectedClass = useMemo(
    () => classes.find((c) => c.id === classChoice) ?? classes[0] ?? null,
    [classes, classChoice],
  );

  const [enrollOpen, setEnrollOpen] = useState(false);

  const classEnrollments = useMemo(
    () =>
      selectedClass === null
        ? []
        : enrollments.filter((e) => e.trainingClassId === selectedClass.id),
    [enrollments, selectedClass],
  );

  if (classes.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('admin.mobile.attendance.noClasses')}
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      {classes.length > 1 && (
        <TextField
          select
          fullWidth
          size="small"
          label={t('admin.mobile.attendance.classLabel')}
          value={String(selectedClass?.id ?? '')}
          onChange={(e) => setClassChoice(Number(e.target.value))}
        >
          {classes.map((c) => (
            <MenuItem key={c.id} value={String(c.id)}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>
      )}

      {selectedClass && (
        <>
          {/* The selected class's roster size, with enrolment right here so a
              new athlete can join the class without leaving the mobile surface. */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
              {t('admin.trainingClasses.athletesLabel', {
                count: classEnrollments.length,
              })}
            </Typography>
            {canManage && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<PersonAddIcon />}
                onClick={() => setEnrollOpen(true)}
              >
                {t('admin.trainingClasses.addAthlete')}
              </Button>
            )}
          </Stack>

          {/* Remounting per class resets the calendar's day and session state. */}
          <AttendanceCalendar
            key={selectedClass.id}
            trainingClass={selectedClass}
            enrollments={classEnrollments}
            athletes={athletes}
            canManage={canManage}
          />

          <TrainingClassMemberDialog
            open={enrollOpen}
            mode="athlete"
            trainingClass={selectedClass}
            takenIds={new Set(classEnrollments.map((e) => e.athleteId))}
            onClose={() => setEnrollOpen(false)}
            onSaved={onSnapshot}
          />
        </>
      )}
    </Stack>
  );
}
